/**
 * @file AppLayout.tsx
 * @description Shared chrome for authenticated pages — brand + nav links
 *   + language toggle + logout. Desktop renders the full nav in a
 *   horizontal row; mobile collapses it behind a hamburger toggle so
 *   the 5+ admin links don't overflow the viewport on a phone.
 * @module Components
 * @security No user input handled here. Admin links are hidden
 *   client-side via useIsAdmin; the API enforces the real role check.
 */

import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { LanguageToggle } from './LanguageToggle';
import { logout } from '../../lib/auth-api';
import { useIsAdmin } from '../../stores/auth.store';

export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const [menuOpen, setMenuOpen] = useState(false);

  // Auto-close the mobile menu on route change so the next page isn't
  // obscured by a stale dropdown after the user taps a link.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const onLogout = async (): Promise<void> => {
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  return (
    <main className="min-h-screen flex flex-col">
      {/* Dark "product chrome" top bar — sticky with a soft blur so
          content scrolling under it reads as depth. */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 sm:px-6 py-3 bg-ink/95 backdrop-blur border-b border-white/10 text-slate-100 shadow-card">
        <Link
          to="/app"
          className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <BrandMark label={t('landing.brand')} onDark />
        </Link>
        <NavRow isAdmin={isAdmin} onLogout={() => void onLogout()} t={t} variant="desktop" />
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-200 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          aria-label={menuOpen ? t('dashboard.menu_close') : t('dashboard.menu_open')}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <HamburgerIcon open={menuOpen} />
        </button>
      </header>
      {menuOpen ? (
        <div
          id="mobile-nav"
          className="md:hidden sticky top-[57px] z-30 border-b border-white/10 bg-ink-soft text-slate-100 shadow-lift"
        >
          <NavRow isAdmin={isAdmin} onLogout={() => void onLogout()} t={t} variant="mobile" />
        </div>
      ) : null}
      <section className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto space-y-6 animate-fade-up">{children}</div>
      </section>
    </main>
  );
}

interface NavRowProps {
  isAdmin: boolean;
  onLogout: () => void;
  t: TFunction;
  variant: 'desktop' | 'mobile';
}

/** Single source of truth for the nav links — rendered horizontally
 *  in the desktop header and stacked inside the mobile dropdown.
 *  Keeps both layouts in lock-step so a new link only needs editing
 *  in one place. */
function NavRow({ isAdmin, onLogout, t, variant }: NavRowProps): JSX.Element {
  const { pathname } = useLocation();
  const wrapCls =
    variant === 'desktop'
      ? 'hidden md:flex items-center gap-1'
      : 'flex flex-col items-stretch p-3 gap-1';
  // Pill-style links on the dark chrome; the active route gets a filled
  // pill so "where am I" is answered at a glance (nav-state-active).
  const base =
    variant === 'desktop'
      ? 'text-sm px-3 py-1.5 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand'
      : 'block py-2 px-3 rounded-md text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';
  const idle = 'text-slate-300 hover:text-white hover:bg-white/10';
  const active = 'text-white bg-white/15 font-medium';
  const linkClsFor = (to: string): string => `${base} ${pathname.startsWith(to) ? active : idle}`;
  const links: Array<{ to: string; label: string; adminOnly?: boolean }> = [
    { to: '/projects', label: t('dashboard.all_projects_link'), adminOnly: true },
    { to: '/admin/invitations', label: t('dashboard.admin_link'), adminOnly: true },
    { to: '/admin/audit', label: t('dashboard.audit_link'), adminOnly: true },
    { to: '/admin/ops', label: t('dashboard.ops_link'), adminOnly: true },
    { to: '/admin/files-review', label: t('dashboard.files_review_link'), adminOnly: true },
    { to: '/admin/quotes-analytics', label: t('dashboard.quotes_analytics_link'), adminOnly: true },
    { to: '/help', label: t('dashboard.help_link') },
    { to: '/settings', label: t('dashboard.settings_link') },
  ];
  return (
    <div className={wrapCls}>
      {links
        .filter((l) => isAdmin || l.adminOnly !== true)
        .map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className={linkClsFor(l.to)}
            aria-current={pathname.startsWith(l.to) ? 'page' : undefined}
          >
            {l.label}
          </Link>
        ))}
      <span
        className={
          variant === 'desktop'
            ? 'ms-2 flex items-center gap-3'
            : 'flex items-center justify-between pt-2 mt-1 border-t border-white/10'
        }
      >
        <LanguageToggle />
        <Button type="button" onClick={onLogout} className="py-1.5">
          {t('dashboard.logout')}
        </Button>
      </span>
    </div>
  );
}

function HamburgerIcon({ open }: { open: boolean }): JSX.Element {
  // Inline SVG — avoids pulling in an icon lib for a single glyph.
  // Toggles between three lines (closed) and an X (open).
  return open ? (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 4L16 16M16 4L4 16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 6h14M3 10h14M3 14h14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
